import { Pipe, PipeTransform } from '@angular/core';

/**
 * Formats a date value with timezone context.
 *
 * Heuristic: if the stored offset is +00:00 (backend UTC-normalized), show browser
 * local time with IANA timezone name. If offset is non-zero (recording device's actual
 * timezone is preserved), show the recording location's local time with UTC offset label.
 *
 * Examples (Asia/Tokyo browser):
 *   "+00:00" input → "2026/04/27 18:05 (Asia/Tokyo)"   ← browser local
 *   "+01:00" input → "2026/04/27 10:05 (UTC+1)"        ← recording location
 */
@Pipe({
  standalone: true,
  name: 'localDatetime',
})
export class LocalDatetimePipe implements PipeTransform {
  transform(value: string | Date | null | undefined): string {
    if (!value) return '';

    if (typeof value === 'string') {
      const offsetMatch = value.match(/([+-])(\d{2}):(\d{2})$/);
      const hasNonZeroOffset =
        offsetMatch &&
        !(offsetMatch[1] === '+' && offsetMatch[2] === '00' && offsetMatch[3] === '00');

      if (hasNonZeroOffset) {
        return this.formatLocationTime(value, offsetMatch);
      }
    }

    return this.formatBrowserLocalTime(value);
  }

  private formatLocationTime(isoString: string, offsetMatch: RegExpMatchArray): string {
    const utcDate = new Date(isoString);
    if (isNaN(utcDate.getTime())) return '';

    const sign = offsetMatch[1] === '+' ? 1 : -1;
    const offsetMin = sign * (parseInt(offsetMatch[2], 10) * 60 + parseInt(offsetMatch[3], 10));
    const locationDate = new Date(utcDate.getTime() + offsetMin * 60_000);

    const year = locationDate.getUTCFullYear();
    const month = String(locationDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(locationDate.getUTCDate()).padStart(2, '0');
    const hours = String(locationDate.getUTCHours()).padStart(2, '0');
    const minutes = String(locationDate.getUTCMinutes()).padStart(2, '0');
    const formatted = `${year}/${month}/${day} ${hours}:${minutes}`;

    const h = parseInt(offsetMatch[2], 10);
    const m = parseInt(offsetMatch[3], 10);
    const tzLabel = m > 0
      ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, '0')}`
      : `UTC${offsetMatch[1]}${h}`;
    return `${formatted} (${tzLabel})`;
  }

  private formatBrowserLocalTime(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value as string);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const formatted = `${year}/${month}/${day} ${hours}:${minutes}`;

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${formatted} (${tz})` : formatted;
    } catch {
      return formatted;
    }
  }
}
